/**
 * Recover the database from the page (piece 21; decision 4040), part 1: the
 * card, Prepare and the comparison. 🔒 Prepare restores a chosen dump into a
 * side database beside the live one, brings its schema forward and shows what
 * a recovery would bring back; it changes nothing live. Part 2 (T2394;
 * decisions 4060 to 4066) is Recover, the restart, the switch-back and Undo.
 */
import { DUMP_PREFIX, dumpSignatureKey, isEncryptedDumpKey } from './backup';
import { unprefixedKey } from './environmentKey';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const RECOVER_PATH = '/settings/storage/recover';
export const RECOVER_DUMPS_PATH = `${RECOVER_PATH}/dumps`;
export const RECOVER_PREPARE_PATH = `${RECOVER_PATH}/prepare`;
export const RECOVER_DISCARD_PATH = `${RECOVER_PATH}/discard`;
/** Decision 4065: Recover, Undo the Recovery, and Done on the Recovered card. */
export const RECOVER_RECOVER_PATH = `${RECOVER_PATH}/recover`;
export const RECOVER_UNDO_PATH = `${RECOVER_PATH}/undo`;
export const RECOVER_DONE_PATH = `${RECOVER_PATH}/done`;

/** 🔒 Decision 4062: the maintenance status, which takes no session and reads the process's flag alone. */
export const MAINTENANCE_PATH = '/health/maintenance';
/** 🔒 Decision 4062: the header every 503 of maintenance carries, which the web shell reads to draw the maintenance page. */
export const MAINTENANCE_HEADER = 'X-BMS2-Maintenance';
/** The window event the shell's one fetch raises on that 503. */
export const MAINTENANCE_EVENT = 'bms2:maintenance';

/** 🔒 Decision 4065: the words a person types to confirm, exactly as written. */
export const RECOVER_CONFIRM_WORD = 'RECOVER';
export const UNDO_CONFIRM_WORD = 'UNDO';

/** 🔒 Decision 4064: the exit code a switch ends the process with; the container's restart policy starts it again. */
export const RECOVER_EXIT_CODE = 75;

/** The days the database from before a recovery stands for Undo, and the seconds a switched start has, where no Settings row says otherwise. */
export const DEFAULT_RECOVER_KEEP_DAYS = 7;
export const DEFAULT_RECOVER_START_TIMEOUT_SECONDS = 300;

/** Recover's steps before the exit, in order (decision 4060); the card names the one under way. */
export const RECOVER_SWITCH_STEPS = ['check', 'safetyDump', 'maintenance', 'connections', 'switch'] as const;
export type RecoverSwitchStep = (typeof RECOVER_SWITCH_STEPS)[number];

/** 🔒 Prepare refuses when the free disk is below this many times the live database's size (decision 4042). */
export const RECOVER_DISK_FACTOR = 2.5;

/** The hours a prepared copy stays where no Settings row says otherwise (decision 4040). */
export const DEFAULT_RECOVER_PREPARE_HOURS = 4;

/** Prepare's steps, in the order it runs them; a run's `due` counts them. */
export const RECOVER_STEPS = ['openCopy', 'fetchDump', 'checkPostgres', 'restore', 'bringForward', 'checkFiles', 'checkSealed', 'compare'] as const;
export type RecoverStep = (typeof RECOVER_STEPS)[number];

/** The card's states (decisions 4040, 4065); each offers one action, Recovered its two. */
export type RecoverState = 'idle' | 'preparing' | 'ready' | 'failed' | 'recovering' | 'recovered';

/** Why a copy that was prepared or failed is gone: Discard, Cancel, the expiry, an API that stopped while it ran, or a recovery that used or replaced it. */
export type RecoverDropReason = 'discarded' | 'canceled' | 'expired' | 'restarted' | 'recovered';

/** The rehearsal's four checks, ticked on the card once each one passed. */
export interface RecoverChecks {
  masterKey: boolean;
  restored: boolean;
  fileOpened: boolean;
  sealedOpened: boolean;
}

/** A type's newest record: its number or name where it has one, and the time its id carries (decision 4041). */
export interface RecoverNewest {
  name: string | null;
  createdAt: string;
}

/**
 * One row of the comparison (decision 4041): a record type of the registry, or
 * file versions, counted in the recovered copy and in the live database, each
 * side's newest record, and what the live data holds that a recovery loses.
 */
export interface RecoverComparisonRow {
  recordType: string;
  recovered: number;
  live: number;
  newestRecovered: RecoverNewest | null;
  newestLive: RecoverNewest | null;
  /** Live records whose id was made after the dump's time; null where the dump's time is unknown. */
  createdAfter: number | null;
  /** Live records made before it that the audit trail records as changed after it, a floor. */
  changedAfter: number | null;
}

/** 🔒 The run's record, kept as JSON text in its `report` (decision 4040). */
export interface RecoverPrepareReport {
  found: string[];
  step: RecoverStep | null;
  sideDatabase: string | null;
  dumpKey: string | null;
  dumpTakenAt: string | null;
  postgres: { dump: string | null; server: string | null };
  schema: { code: number; copy: number; applied: string[] };
  checks: RecoverChecks;
  comparison: RecoverComparisonRow[] | null;
  expiresAt: string | null;
  droppedAt: string | null;
  droppedBecause: RecoverDropReason | null;
  /** Decision 4360: the dump's signature checked, or a dump prepared with none on the super admin's tick; null on a run before it. */
  signature?: 'checked' | 'unsigned' | null;
  /** Decision 4360: the dump was encrypted and opened with the private key typed. */
  encrypted?: boolean;
}

export function emptyRecoverReport(): RecoverPrepareReport {
  return {
    found: [], step: null, sideDatabase: null, dumpKey: null, dumpTakenAt: null,
    postgres: { dump: null, server: null }, schema: { code: 0, copy: 0, applied: [] },
    checks: { masterKey: false, restored: false, fileOpened: false, sealedOpened: false },
    comparison: null, expiresAt: null, droppedAt: null, droppedBecause: null, signature: null, encrypted: false,
  };
}

/** A run's report read back; a report that is not this shape reads as an empty one, so an old row never breaks the card. */
export function parseRecoverReport(text: string | null | undefined): RecoverPrepareReport {
  if (!text) return emptyRecoverReport();
  try {
    const parsed = JSON.parse(text) as Partial<RecoverPrepareReport>;
    return { ...emptyRecoverReport(), ...parsed };
  } catch {
    // silent: a report that is not JSON is a run written before decision 4040's shape, and the card reads it as empty
    return emptyRecoverReport();
  }
}

/** What `GET settings/storage/recover` answers: the state, and what that state shows. */
export interface RecoverStatus {
  state: RecoverState;
  runId: string | null;
  /** The step reached: the one running while Preparing, the one that failed when Failed. */
  step: RecoverStep | null;
  targetName: string | null;
  dumpKey: string | null;
  dumpTakenAt: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  error: string | null;
  checks: RecoverChecks | null;
  comparison: RecoverComparisonRow[] | null;
  migrationsApplied: string[];
  postgres: { dump: string | null; server: string | null } | null;
  /** When the last copy expired and was dropped, read on an Idle card. */
  expiredAt: string | null;
  /** The last refusal this API gave, a sentence. */
  lastRefusal: string | null;
  /** The Organization's zone, which the dumps' and the expiry's times read in. */
  timeZone: string;
  /** Decision 4065: Recover's step under way while Recovering, before the maintenance page takes over. */
  switchStep?: RecoverSwitchStep | null;
  /** Decision 4065: what the Recovered state draws, or null in every other state. */
  recovered?: RecoverRecovered | null;
  /** Decision 4063: `backup.recoverKeepDays`, the days Undo is offered for, which the confirmation names. */
  keepDays?: number;
  /** Decision 4360: the prepared copy came from a dump with no signature, which Recover's confirmation repeats. */
  unsignedDump?: boolean;
}

/** 🔒 Decision 4065: the recovery, or its undo, as the Recovered card shows it. */
export interface RecoverRecovered {
  kind: 'recover' | 'undo';
  runId: string;
  dumpKey: string | null;
  dumpTakenAt: string | null;
  /** When Recover or Undo was pressed, and when the restart finished it. */
  startedAt: string;
  finishedAt: string | null;
  byName: string | null;
  safetyDumpKey: string | null;
  /** The database from before, under the name the switch gave it. */
  previousDatabase: string | null;
  files: { due: number; done: number; failed: number } | null;
  /** Until when Undo the Recovery is offered; null where it is not. */
  undoUntil: string | null;
}

/** One record type's counts as the audit event and the report name them (decision 4066). */
export interface RecoverLostCount {
  recordType: string;
  recovered: number;
  live: number;
  createdAfter: number | null;
  changedAfter: number | null;
}

/** 🔒 A Recover's or a RecoverUndo's run record, kept as JSON text in its `report` (decision 4060). */
export interface RecoverSwitchReport {
  kind: 'recover' | 'undo';
  /** The minute Recover was pressed, which the database from before is named for; an Undo's report keeps its Recover's. */
  stamp: string;
  /** 🔒 On an Undo: the minute Undo was pressed, which the undone database is named for and aged from (decision 4063). */
  undoStamp: string | null;
  found: string[];
  step: RecoverSwitchStep | null;
  dumpKey: string | null;
  dumpTakenAt: string | null;
  safetyDumpKey: string | null;
  /** The database from before, under the name the switch gave it. */
  previousDatabase: string | null;
  sideDatabase: string | null;
  targetId: string | null;
  byName: string | null;
  counts: RecoverLostCount[];
  files: { due: number; done: number; failed: number } | null;
  keepDays: number;
  acknowledgedAt: string | null;
  undoneAt: string | null;
}

export function emptySwitchReport(kind: 'recover' | 'undo', stamp: string): RecoverSwitchReport {
  return {
    kind, stamp, undoStamp: null, found: [], step: null, dumpKey: null, dumpTakenAt: null, safetyDumpKey: null, previousDatabase: null, sideDatabase: null,
    targetId: null, byName: null, counts: [], files: null, keepDays: DEFAULT_RECOVER_KEEP_DAYS, acknowledgedAt: null, undoneAt: null,
  };
}

/** A switch run's report read back; anything not this shape reads as an empty one. */
export function parseSwitchReport(text: string | null | undefined, kind: 'recover' | 'undo' = 'recover'): RecoverSwitchReport {
  if (!text) return emptySwitchReport(kind, '');
  try {
    return { ...emptySwitchReport(kind, ''), ...(JSON.parse(text) as Partial<RecoverSwitchReport>) };
  } catch {
    // silent: a report that is not JSON was written before decision 4060's shape, and reads as empty
    return emptySwitchReport(kind, '');
  }
}

/** The minute a switch is stamped with, in UTC: the same twelve digits the side database's name carries. */
export function recoverStamp(now: Date): string {
  return now.toISOString().slice(0, 16).replace(/[-:T]/g, '');
}

/** 🔒 The names a switch gives the databases (decision 4060): from before, undone, and failed, each the live name, a role and the stamp. */
export function beforeRecoverName(live: string, stamp: string): string {
  return `${live}_before_recover_${stamp}`;
}
export function undoneRecoverName(live: string, stamp: string): string {
  return `${live}_undone_recover_${stamp}`;
}
export function failedRecoverName(live: string, stamp: string): string {
  return `${live}_failed_recover_${stamp}`;
}

/** Whether a stamp is the twelve digits of a minute, the only shape a switch's names carry. */
export function isRecoverStamp(stamp: string | null | undefined): stamp is string {
  return /^\d{12}$/.test(stamp ?? '');
}

/** The time a stamp names, or null for anything that is not twelve digits. */
export function stampTime(stamp: string): Date | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(stamp);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]))) : null;
}

/**
 * 🔒 Decision 4063: the databases the nightly drop may take, each the live name, then `_before_recover_` or
 * `_undone_recover_`, then a stamp, older than the keep days. Every other name is passed over, a failed one included.
 * The stamp is Recover's minute on a database from before and Undo's own minute on an undone one, so each ages from
 * the press that set it aside.
 */
export function recoveredDatabasesToDrop(live: string, names: readonly string[], keepDays: number, now: Date): { name: string; stamp: string; role: 'before' | 'undone' }[] {
  const out: { name: string; stamp: string; role: 'before' | 'undone' }[] = [];
  for (const name of names) {
    for (const role of ['before', 'undone'] as const) {
      const prefix = `${live}_${role}_recover_`;
      if (!name.startsWith(prefix)) continue;
      const stamp = name.slice(prefix.length);
      const at = stampTime(stamp);
      if (at && now.getTime() - at.getTime() > keepDays * 86_400_000) out.push({ name, stamp, role });
    }
  }
  return out;
}

/** 🔒 The step-up code is six digits from the person's own authenticator (decision 4061). */
export function isStepUpCode(code: string | null | undefined): boolean {
  return /^\d{6}$/.test((code ?? '').replace(/\s/g, ''));
}

/** One dump on a Mirror, as Recover to lists it. */
export interface RecoverDump {
  key: string;
  takenAt: string;
  size: number | null;
  newest: boolean;
  /** Decision 4360: the dump is encrypted to the dump key, so Prepare asks for the private half. */
  encrypted: boolean;
  /** Decision 4360: a signature stands beside it; an unsigned dump asks for the tick. */
  signed: boolean;
}

const DUMP_MINUTE = /bms2-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(?:-before-recover)?\.dump(?:\.age)?$/;

/** Whether a key is a dump Recover to lists: the nightly dumps and the safety dumps T2394 writes, plain or encrypted (decision 4360), under any environment's prefix or none (decision 7540). */
export function isRecoverableDumpKey(key: string): boolean {
  const bare = unprefixedKey(key);
  return bare.startsWith(DUMP_PREFIX) && (key.endsWith('.dump') || isEncryptedDumpKey(key)) && !bare.slice(DUMP_PREFIX.length).includes('/');
}

/** The dump's time: the minute its key names, in UTC as the dump wrote it, else the object's own time (decision 4041). */
export function dumpTakenAt(key: string, lastModified: Date | string | null | undefined): string | null {
  const minute = DUMP_MINUTE.exec(key);
  if (minute) return new Date(Date.UTC(Number(minute[1]), Number(minute[2]) - 1, Number(minute[3]), Number(minute[4]), Number(minute[5]))).toISOString();
  return lastModified ? new Date(lastModified).toISOString() : null;
}

/** The listing of the dumps' prefix, newest first by the time each names, the first marked newest. */
export function recoverDumps(objects: readonly { key: string; lastModified: Date | string; size?: number | null }[]): RecoverDump[] {
  const held = new Set(objects.map((o) => o.key));
  const rows = objects
    .filter((o) => isRecoverableDumpKey(o.key))
    .map((o) => ({ key: o.key, takenAt: dumpTakenAt(o.key, o.lastModified) ?? new Date(0).toISOString(), size: o.size ?? null, encrypted: isEncryptedDumpKey(o.key), signed: held.has(dumpSignatureKey(o.key)) }))
    .sort((a, b) => b.takenAt.localeCompare(a.takenAt) || b.key.localeCompare(a.key));
  return rows.map((row, index) => ({ ...row, newest: index === 0 }));
}

/** 🔒 The side database's name: the live database's, then `_recover_` and the minute in UTC (decision 4040). */
export function sideDatabaseName(liveDatabase: string, now: Date): string {
  const minute = now.toISOString().slice(0, 16).replace(/[-:T]/g, '');
  return `${liveDatabase}_recover_${minute}`;
}

/** The prefix every side database of one live database carries, which the sweep reads. */
export function sideDatabasePrefix(liveDatabase: string): string {
  return `${liveDatabase}_recover_`;
}

/** A PostgreSQL version's major number: 16 for "16.13 (Ubuntu …)", 9 for "9.6.24"; null where none is read. */
export function postgresMajor(version: string | null | undefined): number | null {
  const match = /^\s*(\d+)(?:\.(\d+))?/.exec(version ?? '');
  if (!match) return null;
  return Number(match[1]);
}

/**
 * 🔒 The lowest version-7 id made at a moment (decision 4041): every id is
 * `uuid_generate_v7()`, whose first six bytes are milliseconds since the epoch,
 * so an id at or above it was made at or after that moment.
 */
export function uuidV7Floor(at: Date): string {
  const hex = Math.max(0, Math.floor(at.getTime())).toString(16).padStart(12, '0').slice(-12);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-7000-8000-000000000000`;
}

/** The moment a version-7 id was made, or null for an id of any other version. */
export function uuidV7Time(id: string): Date | null {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return null;
  return new Date(parseInt(id.slice(0, 8) + id.slice(9, 13), 16));
}
